import { BOARD_HEIGHT, BOARD_WIDTH, type GameState, type PieceKind } from './game/types';

const GAME_KEY = 'block-clear:game:v2';
const SETTINGS_KEY = 'block-clear:settings:v1';
const PIECES = new Set<PieceKind>(['I', 'O', 'T', 'S', 'Z', 'J', 'L']);

export interface Settings {
  sound: boolean;
  bestScore: number;
}

const defaultSettings: Settings = { sound: true, bestScore: 0 };

export function saveGame(state: GameState): void {
  try {
    localStorage.setItem(GAME_KEY, JSON.stringify(state));
  } catch {
    // Private browsing or storage pressure can make persistence unavailable.
  }
}

export function loadGame(): GameState | undefined {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(GAME_KEY) ?? 'null');
    if (!isGameState(parsed)) return undefined;
    return parsed;
  } catch {
    return undefined;
  }
}

export function loadSettings(): Settings {
  try {
    const parsed = JSON.parse(localStorage.getItem(SETTINGS_KEY) ?? 'null') as Partial<Settings> | null;
    if (!parsed) return { ...defaultSettings };
    return {
      sound: typeof parsed.sound === 'boolean' ? parsed.sound : true,
      bestScore: typeof parsed.bestScore === 'number' && parsed.bestScore >= 0 ? parsed.bestScore : 0,
    };
  } catch {
    return { ...defaultSettings };
  }
}

export function saveSettings(settings: Settings): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // The game remains fully playable without persistent settings.
  }
}

function isGameState(value: unknown): value is GameState {
  if (!value || typeof value !== 'object') return false;
  const state = value as Partial<GameState>;
  const boardValid = Array.isArray(state.board)
    && state.board.length === BOARD_HEIGHT
    && state.board.every((row) => Array.isArray(row)
      && row.length === BOARD_WIDTH
      && row.every((cell) => cell === null || PIECES.has(cell as PieceKind)));
  const activeValid = state.active === null || (
    typeof state.active === 'object'
    && state.active !== null
    && PIECES.has(state.active.kind)
    && Number.isInteger(state.active.rotation)
    && Number.isInteger(state.active.x)
    && Number.isInteger(state.active.y)
  );
  return boardValid
    && activeValid
    && PIECES.has(state.next as PieceKind)
    && typeof state.score === 'number'
    && typeof state.lines === 'number'
    && typeof state.level === 'number'
    && ['idle', 'playing', 'paused', 'over'].includes(state.status ?? '');
}
